import { POPULATION_COLOR_PALETTES } from "@/lib/chartPalettes";

const paletteEntries = Object.entries(POPULATION_COLOR_PALETTES);

export function findPalette(hexColor) {
  if (!hexColor) return null;
  const lower = hexColor.toLowerCase();
  for (const [, shades] of paletteEntries) {
    if (Object.values(shades).some((c) => c.toLowerCase() === lower)) return shades;
  }
  return null;
}

function hexToHsl(hex) {
  const value = String(hex || "").replace("#", "");
  if (![3, 6].includes(value.length)) return null;
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return null;
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

const paletteHues = paletteEntries
  .filter(([name]) => name !== "slate")
  .map(([, shades]) => [shades, hexToHsl(shades[500]).h]);

// Populations saved from the legacy dashboard carry free hex colors: map them to the
// palette closest in hue, greys to slate, so their bars still get readable shades.
export function nearestPalette(hexColor) {
  const hsl = hexToHsl(hexColor);
  if (!hsl) return null;
  if (hsl.s < 0.15) return POPULATION_COLOR_PALETTES.slate;
  let best = null;
  let bestDistance = Infinity;
  for (const [shades, hue] of paletteHues) {
    const diff = Math.abs(hsl.h - hue);
    const distance = Math.min(diff, 360 - diff);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = shades;
    }
  }
  return best;
}

export function paletteFor(hexColor) {
  return findPalette(hexColor) || nearestPalette(hexColor) || POPULATION_COLOR_PALETTES.blue;
}

export function spreadShades(palette, count) {
  if (count <= 1) return [palette[500]];

  let usable;
  if (count <= 3) usable = [700, 600, 500, 400, 300];
  else if (count <= 5) usable = [800, 700, 600, 500, 400, 300, 200];
  else usable = [900, 800, 700, 600, 500, 400, 300, 200, 100];

  const step = (usable.length - 1) / (count - 1);
  return Array.from({ length: count }, (_, i) => palette[usable[Math.round(i * step)]]);
}

export function formatPercent(value) {
  return `${Math.round(value * 100)}%`;
}
